import { List, OrderedMap, Record } from 'immutable';
import { Utils, C } from '@kineticdata/bundle-common';
const { noPayload, withPayload } = Utils;
const ns = Utils.namespaceBuilder('services/settings/portfolios');

export const types = {
  FETCH_PORTFOLIOS_REQUEST: ns('FETCH_PORTFOLIOS_REQUEST'),
  FETCH_PORTFOLIOS_SUCCESS: ns('FETCH_PORTFOLIOS_SUCCESS'),
  FETCH_PORTFOLIOS_FAILURE: ns('FETCH_PORTFOLIOS_FAILURE'),

  FETCH_PORTFOLIO_REQUEST: ns('FETCH_PORTFOLIO_REQUEST'),
  FETCH_PORTFOLIO_SUCCESS: ns('FETCH_PORTFOLIO_SUCCESS'),
  FETCH_PORTFOLIO_FAILURE: ns('FETCH_PORTFOLIO_FAILURE'),

  FETCH_PORTFOLIO_REVISION_REQUEST: ns('FETCH_PORTFOLIO_REVISION_REQUEST'),
  FETCH_PORTFOLIO_REVISION_SUCCESS: ns('FETCH_PORTFOLIO_REVISION_SUCCESS'),
  FETCH_PORTFOLIO_REVISION_FAILURE: ns('FETCH_PORTFOLIO_REVISION_FAILURE'),
  RESET_PORTFOLIO_REVISION_REQUEST: ns('RESET_PORTFOLIO_REVISION_REQUEST'),

  CREATE_PORTFOLIO_REQUEST: ns('CREATE_PORTFOLIO_REQUEST'),
  CLONE_PORTFOLIO_REQUEST: ns('CLONE_PORTFOLIO_REQUEST'),
  EXPIRE_PORTFOLIO_REQUEST: ns('EXPIRE_PORTFOLIO_REQUEST'),
  SAVE_PORTFOLIO_REVISION_REQUEST: ns('SAVE_PORTFOLIO_REVISION_REQUEST'),
  SUBMIT_PORTFOLIO_REVISION_REQUEST: ns('SUBMIT_PORTFOLIO_REVISION_REQUEST'),
  DELETE_PORTFOLIO_REVISION_REQUEST: ns('DELETE_PORTFOLIO_REVISION_REQUEST'),
  LOCK_PORTFOLIO_REVISION_REQUEST: ns('LOCK_PORTFOLIO_REVISION_REQUEST'),

  PORTFOLIO_ACTION_COMPLETE: ns('PORTFOLIO_ACTION_COMPLETE'),
};

export const actions = {
  fetchPortfoliosRequest: noPayload(types.FETCH_PORTFOLIOS_REQUEST),
  fetchPortfoliosSuccess: withPayload(types.FETCH_PORTFOLIOS_SUCCESS),
  fetchPortfoliosFailure: withPayload(types.FETCH_PORTFOLIOS_FAILURE),

  fetchPortfolioRequest: withPayload(types.FETCH_PORTFOLIO_REQUEST),
  fetchPortfolioSuccess: withPayload(types.FETCH_PORTFOLIO_SUCCESS),
  fetchPortfolioFailure: withPayload(types.FETCH_PORTFOLIO_FAILURE),

  fetchPortfolioRevisionRequest: withPayload(
    types.FETCH_PORTFOLIO_REVISION_REQUEST,
    'revisionId',
    'portfolioId',
  ),
  fetchPortfolioRevisionSuccess: withPayload(
    types.FETCH_PORTFOLIO_REVISION_SUCCESS,
  ),
  fetchPortfolioRevisionFailure: withPayload(
    types.FETCH_PORTFOLIO_REVISION_FAILURE,
  ),
  resetPortfolioRevisionRequest: withPayload(
    types.RESET_PORTFOLIO_REVISION_REQUEST,
  ),

  createPortfolioRequest: withPayload(types.CREATE_PORTFOLIO_REQUEST),
  clonePortfolioRequest: withPayload(types.CLONE_PORTFOLIO_REQUEST),
  expirePortfolioRequest: withPayload(types.EXPIRE_PORTFOLIO_REQUEST),
  savePortfolioRevisionRequest: withPayload(
    types.SAVE_PORTFOLIO_REVISION_REQUEST,
  ),
  submitPortfolioRevisionRequest: withPayload(
    types.SUBMIT_PORTFOLIO_REVISION_REQUEST,
  ),
  deletePortfolioRevisionRequest: withPayload(
    types.DELETE_PORTFOLIO_REVISION_REQUEST,
  ),
  lockPortfolioRevisionRequest: withPayload(
    types.LOCK_PORTFOLIO_REVISION_REQUEST,
  ),

  portfolioActionComplete: withPayload(types.PORTFOLIO_ACTION_COMPLETE),
};

export const State = Record({
  data: OrderedMap(),
  dataList: List(),
  dataInit: false,
  dataLoading: false,
  dataError: null,

  current: null,
  currentLoading: false,
  currentError: null,

  revision: null,
  revisionLoading: false,
  revisionError: null,

  actionsPending: 0,
});

const updateDataList = state =>
  state.set(
    'dataList',
    state.data
      .map(l => l.find(p => p.values['Status'] === 'Active') || l.first())
      .toList(),
  );

export const reducer = (state = State(), { type, payload }) => {
  switch (type) {
    case types.FETCH_PORTFOLIOS_REQUEST:
      return state.set('dataError', null).set('dataLoading', true);
    case types.FETCH_PORTFOLIOS_SUCCESS:
      return (
        state
          // Group portfolios by Portfolio Id, sort each group by updatedAt:DESC,
          // and sort the map alphabetically.
          // TODO allow custom sort order
          .set(
            'data',
            List(payload)
              .groupBy(p => p.values[C.FIELD_PORTFOLIO_ID])
              .toOrderedMap()
              .map(l => l.sortBy(p => p.updatedAt).reverse())
              .sortBy(l => l.first().values['Name'].toLowerCase()),
          )
          .set('dataInit', true)
          .set('dataLoading', false)
          // Create a list of all current portofolios
          .update(updateDataList)
      );
    case types.FETCH_PORTFOLIOS_FAILURE:
      return state.set('dataError', payload).set('dataLoading', false);

    case types.FETCH_PORTFOLIO_REQUEST:
      return state
        .update(
          'current',
          current =>
            // If portfolioId changed, set new current portfolio object
            !current || current.values[C.FIELD_PORTFOLIO_ID] !== payload
              ? state.dataList.find(
                  p => p.values[C.FIELD_PORTFOLIO_ID] === payload,
                )
              : current,
        )
        .set('currentError', null)
        .set('currentLoading', true);
    case types.FETCH_PORTFOLIO_SUCCESS:
      return (
        state
          .set('current', payload.record)
          .set('currentLoading', false)
          // Update the list of protfolios for the current id
          .update('data', data =>
            data.set(
              payload.portfolioId,
              List(payload.list)
                .sortBy(p => p.updatedAt)
                .reverse(),
            ),
          )
          // Update list of all current portofolios with latest data
          .update(updateDataList)
      );
    case types.FETCH_PORTFOLIO_FAILURE:
      return state.set('currentError', payload).set('currentLoading', false);

    case types.FETCH_PORTFOLIO_REVISION_REQUEST:
      return state
        .update(
          'revision',
          p =>
            // If revisionId changed, clear revision portfolio object
            !p || p.id !== payload.revisionId ? null : p,
        )
        .set('revisionError', null)
        .set('revisionLoading', true);
    case types.FETCH_PORTFOLIO_REVISION_SUCCESS:
      return (
        state
          .set('revision', payload)
          .set('revisionLoading', false)
          // Update the revision record in the list of portfolios
          .updateIn(
            ['data', payload.values[C.FIELD_PORTFOLIO_ID]],
            list =>
              list
                ? list
                    .map(
                      p =>
                        p.id === payload.id
                          ? // Modify the revision to only have the necessary values
                            // when adding to data list to prevent the redux store
                            // from having a ton of data due to all the Content.
                            {
                              ...payload,
                              values: C.PORTFOLIO_PRIMARY_FIELDS.reduce(
                                (vals, field) => ({
                                  ...vals,
                                  [field]: payload.values[field],
                                }),
                                {},
                              ),
                            }
                          : p,
                    )
                    .sortBy(p => p.updatedAt)
                    .reverse()
                : list,
          )
          // Update list of all current portofolios with latest data
          .update(updateDataList)
      );
    case types.FETCH_PORTFOLIO_REVISION_FAILURE:
      return state.set('revisionError', payload).set('revisionLoading', false);
    case types.RESET_PORTFOLIO_REVISION_REQUEST:
      // If the current revision matches the one we wantt o reset, clear revision state
      return state.getIn(['revision', 'id']) === payload
        ? state
            .set('revision', null)
            .set('revisionLoading', false)
            .set('revisionError', null)
        : state;

    case types.CREATE_PORTFOLIO_REQUEST:
    case types.CLONE_PORTFOLIO_REQUEST:
    case types.EXPIRE_PORTFOLIO_REQUEST:
    case types.SAVE_PORTFOLIO_REVISION_REQUEST:
    case types.SUBMIT_PORTFOLIO_REVISION_REQUEST:
    case types.DELETE_PORTFOLIO_REVISION_REQUEST:
    case types.LOCK_PORTFOLIO_REVISION_REQUEST:
      return state.update('actionsPending', a => a + 1);
    case types.PORTFOLIO_ACTION_COMPLETE:
      return state.update('actionsPending', a => a - 1);

    default:
      return state;
  }
};

export default reducer;

export const selectIsPortfolioOwner = (profile, portfolio) =>
  Utils.isMemberOf(profile || {}, C.SMO_TEAM_NAME) ||
  (!!portfolio &&
    (portfolio.values['Technical Owners'] || []).some(t =>
      Utils.isMemberOf(profile || {}, t),
    ));
